import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import { AppState, AppThunkDispatch } from 'src/store';
import {
  cleanHistoryStylePaneUp,
  receiveHistoryStylePane,
  requestHistoryStylePane,
  receiveError,
  moveItemToHistory,
  localUpdateHistoryStylePaneItem,
  localUpdateAllHistoryStylePaneItems,
  applyHistoryStylePaneAsyncState,
  receiveHistoryStylePaneTenantConfig,
  HistoryStylePaneSlice,
} from './HistoryStylePane.slice';
import HistoryStylePane from './HistoryStylePane';
import container from 'src/ServiceContainer';
import { getProcessedCurrentItem } from './HistoryStylePane.selector';
import { BasicPivotItem } from 'src/worker/pivotWorker.types';
import { StylePane, zStylePane } from 'configuration-schemas/viewdefns/viewdefn';
import { GranularEditPayloadItem } from 'src/dao/pivotClient';
import { ValueSelection } from 'src/pages/AssortmentBuild/StyleEdit/DividedColumnDetailsSection/DividedColumnDetails.types';
import { createSavePayload } from 'src/pages/AssortmentBuild/StyleEdit/DividedColumnDetailsSection/DividedColumnDetails.utils';
import { submitGranular } from 'src/pages/AssortmentBuild/StyleEdit/StyleEditSection/StyleEditSection.utils';
import { updateLifecycleParams } from 'src/pages/AssortmentBuild/StyleEdit/StyleEditSection/StyleEditSection.client';
import { AsyncCellState } from 'src/components/ConfigurableGrid/ConfigurableGrid.types';
import { DaysRangeListResponse } from 'src/types/Scope';
import { flatten, isEmpty, isNil, merge } from 'lodash';
import { resolveStylePaneConfig, STYLE_PANE_COMPONENT } from './HistoryStylePane.types';
import { requestExternalDataRefresh } from '../higherOrder/GlobalDataRefresh';
import { getActiveContext } from 'src/pages/PerspectiveSelection/PerspectiveSelection.selectors';
import type { ContextStylePane } from 'configuration-schemas/confdefn';

export interface StateProps extends HistoryStylePaneSlice {
  // week range data drilled down for 'rangePickerEditor' fields
  mergedRangeList: DaysRangeListResponse;
  allowFrom: string;
  allowTo: string;
  stylePaneConfig?: ContextStylePane;
}

export interface UpdateLifecycleDataArgs {
  styleColorId: string;
  newValue: { [s: string]: any };
  defnId: string;
  aggBy: string[];
  position: number;
}

export interface SaveDataFromPayloadsArgs {
  payloads: GranularEditPayloadItem[];
  styleId: string;
  dataIndex: string;
  updatedValue: ValueSelection['value'];
  updateCoordinateMap: Record<string, string> | undefined;
  row: BasicPivotItem;
  productId: string;
  defnId: string;
  aggBy: string[];
  position: number;
}

// the keys a set of payloads will mutate (everything but their coordinates),
// used to mark those cells as async-busy
function getPayloadsUpdateKeys(payloads: GranularEditPayloadItem[]): string[] {
  return flatten(payloads.map((p) => Object.keys(p).filter((key) => key !== 'coordinates')));
}

function mapStateToProps(state: AppState): StateProps {
  const { daysRangeListExtended, daysPastRangeList } = state.scope;
  // merge assortment + history range lists (see RangePickerEditor.container's mergeProps)
  const mergedRangeList: DaysRangeListResponse = {
    start_date: merge({}, daysRangeListExtended.start_date, daysPastRangeList.start_date),
    end_date: merge({}, daysRangeListExtended.end_date, daysPastRangeList.end_date),
  };

  // derive the full selectable window from the merged list (see RangePickerEditor)
  let allowFrom = '';
  let allowTo = '';
  const rangeListToUse = mergedRangeList.end_date;
  const rangeKeys = Object.keys(rangeListToUse);
  if (!isEmpty(rangeKeys)) {
    const orderedDates = rangeKeys.sort((a, k) => Number(a.replace(/-/g, '')) - Number(k.replace(/-/g, '')));
    allowFrom = rangeListToUse[orderedDates[0]];
    allowTo = rangeListToUse[orderedDates[orderedDates.length - 1]];
  }

  return {
    ...state.historyStylePane,
    currentItem: getProcessedCurrentItem(state),
    mergedRangeList,
    allowFrom,
    allowTo,
    stylePaneConfig: getActiveContext(state)?.stylePane,
  };
}

// Redecorate every style-color row currently rendered in the pane. Editing a single
// item can affect sibling rows, so we always refresh the whole pane.
async function redecorateStylePane(
  dispatch: AppThunkDispatch,
  currentItem: BasicPivotItem[],
  updateKeys: string[],
  defnId: string,
  aggBy: string[],
) {
  if (isNil(currentItem) || isEmpty(currentItem)) {
    return;
  }

  // Tell host views (for example a stylecolor-level ConfigurableGrid) to redecorate the
  // same style-colors so their data stays consistent with the pane. `updateKeys` scopes
  // which cells the host should mark busy; the pane always refreshes every color it shows.
  // We do this just before the Style Pane redecorate hoping that they'l both be
  // successfull and it'll update faster
  dispatch(
    requestExternalDataRefresh({
      sourceComponent: STYLE_PANE_COMPONENT,
      styleIds: [currentItem[0].style], // only ever 1 style for style pane
      stylecolorIds: currentItem.map((item) => item.id),
      dataIndices: updateKeys,
    }),
  );
  const redecoratedData = await container.pivotService.redecorate({
    coordinates: currentItem.map((item) => ({
      stylecolor: item.stylecolor ?? '',
      channel: item.channel ?? '',
    })),
    defnId,
    nestData: false,
    aggBy,
  });
  if (!isNil(redecoratedData)) {
    // splice every refreshed row back into redux in a single reducer pass
    dispatch(localUpdateAllHistoryStylePaneItems(redecoratedData));
  }
}

function dispatchToProps(dispatch: AppThunkDispatch) {
  const service = container.pivotService;
  const client = container.tenantConfigClient;

  return {
    onShowHistoryStylePane(memberId: string, isAssortmentBuild: boolean, stylePane?: ContextStylePane) {
      const resolvedConfig = resolveStylePaneConfig(stylePane, isAssortmentBuild);
      dispatch(requestHistoryStylePane());
      client
        .getTenantViewDefns<StylePane>({
          defnIds: [resolvedConfig.viewDefnId],
          appName: resolvedConfig.appName,
          validationSchemas: [zStylePane],
        })
        .then((resp) => {
          if (resp !== null && Array.isArray(resp) && resp.length > 0) {
            dispatch(receiveHistoryStylePaneTenantConfig(resp[0]));
            return;
          }
          dispatch(receiveError("Couldn't load configs"));
        });

      const options = {
        ...resolvedConfig.dataParams,
        aggBy: resolvedConfig.aggBy.join(','),
        topMembers: memberId,
        nestData: false,
      };

      service.getStylePaneDetails(resolvedConfig.dataDefnId, options).then((resp) => {
        if (resp !== null) {
          dispatch(receiveHistoryStylePane(resp));
          return;
        }
        dispatch(receiveError("Couldn't load pivot results"));
      });
    },
    moveItemToHistory(memberId: string, item: BasicPivotItem[]) {
      dispatch(moveItemToHistory(memberId, item));
    },
    localUpdateItem(item: BasicPivotItem, position: number) {
      dispatch(localUpdateHistoryStylePaneItem(item, position));
    },
    saveDataFromPayloads(args: SaveDataFromPayloadsArgs, currentItem: BasicPivotItem[]) {
      const { payloads, styleId, dataIndex, updatedValue, updateCoordinateMap, row, defnId, aggBy, position } = args;
      // Build the dropdown's own save payload then submit it alongside any
      // dependent-calc payloads computed by the caller.
      const dropdownPayload = createSavePayload(styleId, dataIndex, updatedValue, updateCoordinateMap, row);
      const allPayloads = dropdownPayload != null ? [...payloads, dropdownPayload] : payloads;
      const updateKeys = getPayloadsUpdateKeys(allPayloads);
      // mark affected cells busy while the save is in flight
      dispatch(applyHistoryStylePaneAsyncState(updateKeys, AsyncCellState.Processing, position));
      return submitGranular(allPayloads)
        .then(async () => {
          dispatch(applyHistoryStylePaneAsyncState(updateKeys, AsyncCellState.Redecorating, position));
          await redecorateStylePane(dispatch, currentItem, updateKeys, defnId, aggBy);
        })
        .catch((e) => {
          toast.error('An error occured updating your style');
          container.loggingService.error('Error in granular update', e.stack);
        })
        .finally(() => {
          dispatch(applyHistoryStylePaneAsyncState(updateKeys, AsyncCellState.Idle, position));
        });
    },
    updateLifecycleData(args: UpdateLifecycleDataArgs, currentItem: BasicPivotItem[]) {
      const { styleColorId, newValue, defnId, aggBy, position } = args;
      const updateKeys = Object.keys(newValue);
      // mark affected cells busy while the save is in flight
      dispatch(applyHistoryStylePaneAsyncState(updateKeys, AsyncCellState.Processing, position));
      return updateLifecycleParams(styleColorId, newValue)
        .then(async () => {
          dispatch(applyHistoryStylePaneAsyncState(updateKeys, AsyncCellState.Redecorating, position));
          await redecorateStylePane(dispatch, currentItem, updateKeys, defnId, aggBy);
        })
        .catch((e) => {
          toast.error('An error occured updating your style');
          container.loggingService.error('Error in lifecycle params update', e.stack);
        })
        .finally(() => {
          dispatch(applyHistoryStylePaneAsyncState(updateKeys, AsyncCellState.Idle, position));
        });
    },
    onDestroy() {
      dispatch(cleanHistoryStylePaneUp());
    },
  };
}

export type DispatchProps = ReturnType<typeof dispatchToProps>;

export default connect(mapStateToProps, dispatchToProps)(HistoryStylePane);
